import { STAFF_ROLES, type StaffRole } from "@puszczyk/contracts";
import { useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, unwrap } from "../../lib/api";
import { meQuery } from "../../lib/me";
import { useApiMutation } from "../../lib/mutation";
import { Badge } from "../../ui/Badge";
import { Button } from "../../ui/Button";
import { Input } from "../../ui/Input";
import { Select } from "../../ui/Select";
import { toast } from "../../ui/toast";

type Member = {
  userId: string;
  name: string;
  email: string;
  role: string;
  twoFactorEnabled: boolean;
};
type Draft = { name: string; email: string; role: StaffRole };

/** One staff account, with an in-place fix of name, e-mail and role by the admin (#232). */
export function MemberRow({
  organizationId,
  member,
  onSaved,
}: {
  organizationId: string;
  member: Member;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Draft | null>(null);
  const save = useApiMutation(
    (json: Draft) =>
      unwrap(
        api.organizations[":organizationId"].members[":userId"].$patch({
          param: { organizationId, userId: member.userId },
          json,
        }),
      ),
    {
      onSuccess: () => {
        toast(t("common.saved"));
        setDraft(null);
        onSaved();
        // The header shows the caller's own name and organizations.
        void queryClient.invalidateQueries({ queryKey: meQuery.queryKey });
      },
    },
  );
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (draft) save.run(draft);
  };

  if (draft)
    return (
      <li className="px-4 py-2">
        <form onSubmit={submit} className="flex flex-wrap items-center gap-2">
          <Input
            aria-label={t("members.name")}
            required
            minLength={2}
            className="min-w-40 flex-1"
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          />
          <Input
            aria-label={t("auth.email")}
            type="email"
            required
            className="min-w-48 flex-1"
            value={draft.email}
            onChange={(e) => setDraft({ ...draft, email: e.target.value })}
          />
          <Select
            aria-label={t("members.role")}
            value={draft.role}
            onChange={(e) => setDraft({ ...draft, role: e.target.value as StaffRole })}
          >
            {STAFF_ROLES.map((r) => (
              <option key={r} value={r}>
                {t(`staffRole.${r}`)}
              </option>
            ))}
          </Select>
          <Button type="submit" size="sm" variant="primary" disabled={save.isPending}>
            {t("common.save")}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setDraft(null)}>
            {t("common.cancel")}
          </Button>
        </form>
      </li>
    );
  return (
    <li className="flex flex-wrap items-center gap-3 px-4 py-2">
      <span className="font-medium">{member.name}</span>
      <span className="text-zinc-400">{member.email}</span>
      <Badge className="ml-auto">{t(`staffRole.${member.role}`)}</Badge>
      {member.twoFactorEnabled ? (
        <Badge tone="green">2FA</Badge>
      ) : (
        <Badge tone="yellow">{t("members.no2fa")}</Badge>
      )}
      <Button
        size="sm"
        variant="ghost"
        onClick={() =>
          setDraft({ name: member.name, email: member.email, role: member.role as StaffRole })
        }
      >
        {t("common.edit")}
      </Button>
    </li>
  );
}
